import type { LeadershipStaffRole, StaffRole, LegislativeRoles } from 'services/person/us/edit/legislativeRolesService'
import { type Prefix } from 'services'
import { type ExecCandidate } from 'services/candidate/candidateService'
import { type ExecutivePosition } from 'services/person/us/edit/executiveRolesService'

export function preparePayload (payload: any): any {
  const newPayload = { ...payload }
  const nameFields = ['nickname', 'firstName', 'middleName', 'lastName']

  nameFields.forEach((field) => {
    if (newPayload[field] != null) {
      newPayload[field] = sanitizeName(newPayload[field])
    }
  })

  return newPayload
}

function sanitizeName (name: string): string {
  return name
    .replace(/\u00AD/g, '') // Soft hyphen
    .replace(/\u200B/g, '') // Zero width space
    .replace(/\u200C/g, '') // Zero width non joiner
    .replace(/\u200D/g, '') // Zero width joiner
    .replace(/\uFEFF/g, '') // Byte order mark
    .trim() // Remove leading/trailing whitespace
}

export function resolveNickname (nickname?: string, firstName?: string): string | undefined {
  return nickname == null || nickname.trim() === '' ? firstName : nickname
}

export function getMemberFromStaffRole (role: StaffRole): string {
  return (role.member?.name !== undefined && role.member.name !== null && role.member.name !== '')
    ? `${(role.member.role !== undefined && role.member.role !== null && role.member.role !== '') ? role.member.role : ''} ${role.member.name}`
    : role.role?.desc ?? ''
}

export function getOfficeFromLeadershipStaffRole (role: LeadershipStaffRole): string {
  const officeName = role.leadership_officer?.ofcname ?? ''
  const chamberDesc = role.leadership_officer?.legchamber?.desc ?? ''
  const stateDesc = role.leadership_officer?.state?.state_desc ?? ''

  let primaryText = officeName
  if (chamberDesc !== '' && stateDesc !== '') {
    primaryText += ` (${chamberDesc}${stateDesc !== 'All' ? `, ${stateDesc}` : ', U.S'})`
  }

  return primaryText
}

export function createEmptyPrefix (code: string): Prefix {
  return {
    id: 0,
    code,
    desc: '',
    lutOrder: 0,
    updatedBy: '',
    lastUpdated: null
  }
}

export function extractRoleIds (
  legislativeRoles: LegislativeRoles,
  execCandidates: ExecCandidate[],
  executivePositions: ExecutivePosition[] = []
): number[] {
  const roleIds = new Set<number>()

  // Member roles
  legislativeRoles.member_roles.forEach(role => {
    if (role.legrole?.role?.id !== undefined && role.legrole.role.id !== null && role.legrole.role.id !== 0) {
      roleIds.add(role.legrole.role.id)
    }
  })

  // Staff roles
  legislativeRoles.member_staff_roles.forEach(role => {
    if (role.role?.id !== undefined && role.role.id !== null && role.role.id !== 0) {
      roleIds.add(role.role.id)
    }
  })

  // Committee staff roles
  legislativeRoles.committee_staff_roles.forEach(role => {
    if (role.role?.id !== undefined && role.role.id !== null && role.role.id !== 0) {
      roleIds.add(role.role.id)
    }
  })

  // Committee member roles
  legislativeRoles.committee_members.forEach(member => {
    if (member.role?.id !== undefined && member.role.id !== null && member.role.id !== 0) {
      roleIds.add(member.role.id)
    }
  })

  // Caucus member roles
  legislativeRoles.caucus_members.forEach(member => {
    if (member.role?.id !== undefined && member.role.id !== null && member.role.id !== 0) {
      roleIds.add(member.role.id)
    }
  })

  // Leadership roles
  legislativeRoles.leadership_roles.forEach(role => {
    if (role.role?.id !== undefined && role.role.id !== null && role.role.id !== 0) {
      roleIds.add(role.role.id)
    }
  })

  // Leadership staff roles
  legislativeRoles.leadership_staff_roles.forEach(role => {
    if (role.legrole?.role?.id !== undefined && role.legrole.role.id !== null && role.legrole.role.id !== 0) {
      roleIds.add(role.legrole.role.id)
    }
  })

  // Legislative candidate roles
  legislativeRoles.leg_candidates.forEach(candidate => {
    if (candidate.role_id !== undefined && candidate.role_id !== null && candidate.role_id !== 0) {
      roleIds.add(candidate.role_id)
    }
  })

  // Executive candidate roles
  execCandidates.forEach(candidate => {
    if (candidate.role_id !== undefined && candidate.role_id !== null && candidate.role_id !== 0) {
      roleIds.add(candidate.role_id)
    }
    if (candidate.exec_role_id !== undefined && candidate.exec_role_id !== null && candidate.exec_role_id !== 0) {
      roleIds.add(candidate.exec_role_id)
    }
  })

  executivePositions.forEach(position => {
    const execRoles = [position.execRole, position.execRole2, position.execRole3]
    execRoles.forEach(execRole => {
      const generalRoleId = execRole?.role?.id
      if (generalRoleId !== undefined && generalRoleId !== null && generalRoleId !== 0) {
        roleIds.add(generalRoleId)
      }
    })
  })

  return Array.from(roleIds)
}
